/**
 * Pipeline strip: the stages of the platform in a row, each with its live
 * count and one qualifying line, joined by chevrons (task 68).
 *
 * Events → Detections → Playbook → Alerts (2026-09-29, task 158: Events
 * replaced Sources as the intake stage). The tiles run across from `lg`
 * up — five would not fit at `md` — and sit in a two-column grid below it, where the chevrons
 * drop out because a wrapped arrow points at nothing; an odd last tile
 * spans both columns. Tailwind needs literal class names, so each stage
 * count the strip supports has its own template.
 */

import type { ReactNode } from 'react';

import { MetricTile } from './MetricTile.js';
import { colors } from './tokens.js';

export interface PipelineStage {
  label: string;
  value: number | string;
  sub?: ReactNode;
  color?: string;
  tint?: string;
  /** Makes the stage a link to its section; the tile becomes keyboard-reachable. */
  onClick?: () => void;
}

export interface PipelineStripProps {
  stages: PipelineStage[];
  className?: string;
}

const ACROSS: Record<number, string> = {
  3: 'lg:grid-cols-[1fr_auto_1fr_auto_1fr]',
  4: 'lg:grid-cols-[1fr_auto_1fr_auto_1fr_auto_1fr]',
  5: 'lg:grid-cols-[1fr_auto_1fr_auto_1fr_auto_1fr_auto_1fr]',
  6: 'lg:grid-cols-[1fr_auto_1fr_auto_1fr_auto_1fr_auto_1fr_auto_1fr]',
};

export function PipelineStrip({ stages, className }: PipelineStripProps) {
  const odd = stages.length % 2 === 1;
  return (
    <div
      className={[
        'grid grid-cols-2 items-stretch gap-3 lg:items-center',
        ACROSS[stages.length] ?? '',
        className ?? '',
      ].join(' ')}
    >
      {stages.map((stage, i) => (
        <StageCell
          key={stage.label}
          stage={stage}
          last={i === stages.length - 1}
          span={odd && i === stages.length - 1}
        />
      ))}
    </div>
  );
}

function StageCell({
  stage,
  last,
  span,
}: {
  stage: PipelineStage;
  last: boolean;
  span: boolean;
}) {
  return (
    <>
      <MetricTile
        label={stage.label}
        value={stage.value}
        sub={stage.sub}
        color={stage.color}
        tint={stage.tint}
        onClick={stage.onClick}
        className={span ? 'col-span-2 h-full lg:col-span-1' : 'h-full'}
      />
      {!last && (
        <span
          aria-hidden="true"
          className="hidden lg:flex lg:items-center"
          style={{ color: colors.muted }}
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path
              d="M6 3l5 5-5 5"
              stroke="currentColor"
              strokeWidth="1.75"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
      )}
    </>
  );
}
